<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="dialogStatus"
      persistent
      content-class="warranty-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text"
        >
          <template v-if="lodash.isEmpty(warrantyDetail) === false">
            Update Warranty
          </template>
          <template v-else> Add Warranty</template>
          - <span class="orange--text">{{ warrantyOption.unique_id }}</span>
        </v-card-title>
        <v-card-text class="px-6 font-size-16">
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
            style="max-height: calc(100vh - 280px); position: relative"
          >
            <v-form
              ref="warrantyForm"
              v-model.trim="formValid"
              lazy-validation
              v-on:submit.stop.prevent="updateWarranty"
            >
              <v-container fluid class="px-0 py-0">
                <!-- Section 1: Category & Asset Selection -->
                <div class="erp-form-section mb-6">
                  <div class="section-title d-flex align-center">
                    <v-icon small color="cyan darken-2" class="mr-2"
                      >mdi-layers-outline</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold text-uppercase custom-header-blue-text"
                      >Warranty Category & Asset Selection</span
                    >
                  </div>

                  <v-row>
                    <template v-if="lodash.isEmpty(warrantyDetail)">
                      <v-col md="12" cols="12" class="py-1 mb-2">
                        <RadioInputCustom
                          v-on:change="getOptions"
                          v-model="warrantyOption.equipemt_type"
                          row
                          :items="[
                            {
                              label: 'Equipment',
                              color: 'red darken-3 white--text',
                              value: 'customer-asset',
                            },
                            {
                              label: 'Own Asset',
                              color: 'orange darken-4 white--text',
                              value: 'asset',
                            },
                          ]"
                        />
                      </v-col>

                      <!-- Customer Field -->
                      <v-col
                        md="6"
                        cols="12"
                        class="py-2"
                        v-if="warrantyOption.equipemt_type == 'customer-asset'"
                      >
                        <label
                          class="font-size-16 font-weight-500 d-block mb-1"
                        >
                          Customer
                        </label>
                        <v-autocomplete
                          v-model.trim="warrantyOption.customer"
                          :items="allCustomerList"
                          clearable
                          dense
                          filled
                          solo
                          flat
                          v-on:click:clear="clearAll"
                          placeholder="Select Customer"
                          item-color="cyan"
                          color="cyan"
                          :disabled="pageLoading"
                          :loading="pageLoading"
                          item-text="display_name"
                          item-value="id"
                          v-on:change="getOptions"
                          hide-details="auto"
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-title
                                v-html="'No Customer(s) Found.'"
                              ></v-list-item-title>
                            </v-list-item>
                          </template>
                          <template v-slot:item="{ item }">
                            <v-list-item-avatar size="32">
                              <v-img
                                :lazy-src="$defaultProfileImage"
                                :src="$assetAPIURL(item.profile_logo)"
                                aspect-ratio="1"
                                class="grey lighten-5"
                              ></v-img>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title
                                class="text-capitalize font-weight-medium text-body-1"
                              >
                                {{ item.display_name }}
                                <v-chip
                                  color="blue white--text"
                                  label
                                  x-small
                                  class="ml-2 font-weight-bold"
                                  v-if="
                                    item.company_type &&
                                    item.company_type == 'company'
                                  "
                                >
                                  Company
                                </v-chip>
                                <v-chip
                                  x-small
                                  color="red white--text"
                                  label
                                  class="ml-2 font-weight-bold"
                                  v-if="
                                    item.company_type &&
                                    item.company_type == 'personal'
                                  "
                                >
                                  Individual
                                </v-chip>
                              </v-list-item-title>
                              <v-list-item-subtitle
                                class="text-caption text--secondary customer-option-subtitle"
                                v-if="item.company_name"
                              >
                                {{ item.company_name }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <!-- Site Location Field -->
                      <v-col
                        md="6"
                        cols="12"
                        class="py-2"
                        v-if="warrantyOption.equipemt_type == 'customer-asset'"
                      >
                        <label
                          class="font-size-16 font-weight-500 d-block mb-1"
                        >
                          Site Location
                        </label>
                        <v-autocomplete
                          hide-details="auto"
                          clearable
                          id="property-equipment"
                          :items="allSiteLocationList"
                          v-on:change="getOptions($event, 'location')"
                          v-model.trim="warrantyOption.address"
                          dense
                          filled
                          solo
                          flat
                          placeholder="Select Site Location"
                          item-color="cyan"
                          color="cyan"
                          :disabled="pageLoading"
                          :loading="pageLoading"
                          item-text="barcode"
                          item-value="id"
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-title
                                v-html="'No Site Location(s) Found.'"
                              ></v-list-item-title>
                            </v-list-item>
                          </template>
                          <template v-slot:item="{ item }">
                            <v-list-item-icon class="mr-3">
                              <v-icon color="cyan darken-2"
                                >mdi-home-map-marker</v-icon
                              >
                            </v-list-item-icon>
                            <v-list-item-content>
                              <div class="d-flex align-center mb-1">
                                <Barcode
                                  class="my-0 mr-2"
                                  :barcode="item.barcode"
                                />
                                <v-chip
                                  v-if="item.is_tenant"
                                  label
                                  x-small
                                  color="green"
                                  outlined
                                  class="mr-1"
                                  >Tenant</v-chip
                                >
                                <v-chip
                                  label
                                  x-small
                                  :color="item.type == 1 ? 'cyan' : 'orange'"
                                  outlined
                                >
                                  {{
                                    item.type == 1 ? "Site Location" : "Billing"
                                  }}
                                </v-chip>
                              </div>
                              <v-list-item-title
                                class="text-body-1 font-weight-medium"
                              >
                                {{ get_formatted_property(item) }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <!-- Parts Field -->
                      <v-col
                        md="12"
                        cols="12"
                        class="py-2"
                        v-if="warrantyOption.equipemt_type == 'parts'"
                      >
                        <label
                          class="font-size-16 font-weight-500 d-block mb-1 required"
                        >
                          Parts
                        </label>
                        <v-autocomplete
                          :filter="customFilter"
                          v-model="warrantyOption.equipemt"
                          :items="allPartLists"
                          clearable
                          dense
                          filled
                          solo
                          flat
                          placeholder="Select Parts"
                          v-on:change="getPart($event)"
                          item-color="cyan"
                          color="cyan"
                          :disabled="pageLoading"
                          :loading="pageLoading"
                          item-text="name"
                          item-value="id"
                          :rules="[
                            validateRules.required(
                              warrantyOption.equipemt,
                              'Parts'
                            ),
                          ]"
                          hide-details="auto"
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-title
                                v-html="'No Parts(s) Found.'"
                              ></v-list-item-title>
                            </v-list-item>
                          </template>
                          <template v-slot:item="{ item }">
                            <v-avatar left size="32" class="mr-3">
                              <v-img
                                v-if="
                                  item.part_image &&
                                  !lodash.isEmpty(item.part_image) &&
                                  !lodash.isEmpty(item.part_image.file)
                                "
                                :src="item.part_image.file.url"
                              ></v-img>
                              <v-img v-else :src="$defaultImage"></v-img>
                            </v-avatar>
                            <v-list-item-content>
                              <v-list-item-title
                                class="text-body-1 font-weight-medium"
                              >
                                {{ item.name }}
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-caption">
                                {{ item.barcode }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <!-- Equipment Field -->
                      <v-col
                        md="12"
                        cols="12"
                        class="py-2"
                        v-if="warrantyOption.equipemt_type != 'parts'"
                      >
                        <label
                          class="font-size-16 font-weight-500 d-block mb-1 required"
                        >
                          <template
                            v-if="warrantyOption.equipemt_type == 'tool'"
                            >Tool</template
                          >
                          <template
                            v-else-if="
                              warrantyOption.equipemt_type == 'customer-asset'
                            "
                            >Equipment</template
                          >
                          <template v-else>Own Asset</template>
                        </label>
                        <v-autocomplete
                          :filter="customFilter"
                          v-model="warrantyOption.equipemt"
                          :items="allEquipemtList"
                          return-object
                          clearable
                          dense
                          filled
                          solo
                          flat
                          :placeholder="
                            getPlaceHolder(warrantyOption.equipemt_type)
                          "
                          v-on:change="getEquipment($event)"
                          item-color="cyan"
                          color="cyan"
                          :disabled="pageLoading"
                          :loading="pageLoading"
                          item-text="name"
                          :rules="[
                            validateRules.required(
                              warrantyOption.equipemt,
                              'Assets'
                            ),
                          ]"
                          hide-details="auto"
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-title
                                v-html="'No Assets(s) Found.'"
                              ></v-list-item-title>
                            </v-list-item>
                          </template>
                          <template v-slot:item="{ item }">
                            <v-avatar left size="32" class="mr-3">
                              <v-img
                                v-if="item.image"
                                :src="item.image"
                              ></v-img>
                              <v-img v-else :src="$defaultImage"></v-img>
                            </v-avatar>
                            <v-list-item-content>
                              <v-list-item-title
                                class="text-body-1 font-weight-medium d-flex align-center"
                              >
                                {{ item.name }}
                                <v-chip
                                  v-if="
                                    item?.warranty?.status &&
                                    item.has_warranty &&
                                    item.has_warranty == 1
                                  "
                                  :color="getColor(item?.warranty?.status)"
                                  label
                                  x-small
                                  outlined
                                  class="ml-2 font-weight-bold"
                                >
                                  {{ item?.warranty?.barcode }}
                                </v-chip>
                              </v-list-item-title>
                              <v-list-item-subtitle
                                class="text-caption-1 d-flex align-center customer-option-subtitle"
                              >
                                {{ item.barcode }}
                                <v-chip
                                  v-if="item.type && item.type == 'company'"
                                  color="blue white--text"
                                  label
                                  x-small
                                  class="ml-2 font-weight-bold"
                                >
                                  {{ appCompanyName }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </template>

                    <!-- Selected Equipment Summary Display Card -->
                    <v-col
                      md="12"
                      cols="12"
                      class="py-2"
                      v-if="
                        warrantyOption.equipemt && warrantyOption.equipemt_type
                      "
                    >
                      <v-card
                        outlined
                        class="grey lighten-5 rounded-lg pa-4 border-dashed"
                      >
                        <v-row align="center">
                          <!-- Image Column -->
                          <v-col cols="12" sm="2" class="text-center">
                            <v-avatar size="72" class="elevation-1 white">
                              <v-img
                                v-if="
                                  warrantyOption.equipemt_type == 'parts' &&
                                  partInfo &&
                                  partInfo.part_image &&
                                  partInfo.part_image.file &&
                                  partInfo.part_image.file.url
                                "
                                :src="partInfo.part_image.file.url"
                                contain
                              ></v-img>
                              <v-img
                                v-else-if="equipmentInfo && equipmentInfo.image"
                                :src="equipmentInfo.image"
                                contain
                              ></v-img>
                              <v-img
                                v-else
                                :src="$defaultImage"
                                contain
                              ></v-img>
                            </v-avatar>
                          </v-col>

                          <!-- Primary Details Column -->
                          <v-col cols="12" sm="5">
                            <div class="info-group">
                              <div
                                class="d-flex align-center mb-1"
                                v-if="
                                  warrantyOption.equipemt_type == 'parts'
                                    ? partInfo.barcode
                                    : equipmentInfo.barcode
                                "
                              >
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >ID / Barcode:</span
                                >
                                <v-chip
                                  color="cyan white--text"
                                  label
                                  x-small
                                  class="font-weight-bold"
                                >
                                  {{
                                    warrantyOption.equipemt_type == "parts"
                                      ? partInfo.barcode
                                      : equipmentInfo.barcode
                                  }}
                                </v-chip>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="
                                  warrantyOption.equipemt_type == 'parts'
                                    ? partInfo.serial_no
                                    : equipmentInfo.serial_no
                                "
                              >
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >Serial No:</span
                                >
                                <span
                                  class="text-body-1 font-weight-medium text--primary"
                                >
                                  {{
                                    warrantyOption.equipemt_type == "parts"
                                      ? partInfo.serial_no
                                      : equipmentInfo.serial_no
                                  }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center"
                                v-if="
                                  warrantyOption.equipemt_type == 'parts'
                                    ? partInfo.name
                                    : equipmentInfo.name
                                "
                              >
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >Name:</span
                                >
                                <span
                                  class="text-body-1 font-weight-bold text--primary"
                                >
                                  {{
                                    warrantyOption.equipemt_type == "parts"
                                      ? partInfo.name
                                      : equipmentInfo.name
                                  }}
                                </span>
                              </div>
                            </div>
                          </v-col>

                          <!-- Secondary Details Column -->
                          <v-col cols="12" sm="5">
                            <div class="info-group">
                              <div class="d-flex align-center mb-1">
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >Brand:</span
                                >
                                <span class="text-body-1 text--primary">
                                  {{
                                    (warrantyOption.equipemt_type == "parts"
                                      ? partInfo.brand
                                      : equipmentInfo.brand) || "No brand"
                                  }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="
                                  warrantyOption.equipemt_type == 'parts' &&
                                  (partInfo.part_number ||
                                    !lodash.isEmpty(partInfo.product_prefix))
                                "
                              >
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >Part No:</span
                                >
                                <span class="text-body-1 text--primary">
                                  {{
                                    partInfo.product_prefix +
                                    "-" +
                                    partInfo.product_prefix_no
                                  }}
                                </span>
                              </div>

                              <div
                                class="d-flex align-center mb-1"
                                v-if="
                                  warrantyOption.equipemt_type != 'parts' &&
                                  equipmentInfo.do_no
                                "
                              >
                                <span
                                  class="font-weight-bold text-caption-1 text--secondary mr-2"
                                  >DO No:</span
                                >
                                <span class="text-body-1 text--primary">{{
                                  equipmentInfo.do_no
                                }}</span>
                              </div>
                            </div>
                          </v-col>
                        </v-row>

                        <!-- Contact & Address Sub-Section for Customer Equipment -->
                        <v-row
                          class="mt-2 pt-2 border-top"
                          v-if="
                            warrantyOption.equipemt_type == 'customer-asset' &&
                            equipmemntArr
                          "
                        >
                          <v-col cols="12" sm="6">
                            <div
                              class="text-caption-1 font-weight-bold text-uppercase text--secondary mb-1"
                            >
                              Contact Details
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="
                                equipmemntArr.contact_person &&
                                equipmemntArr.contact_person.display_name
                              "
                            >
                              <v-icon small color="cyan darken-2" class="mr-2"
                                >mdi-account</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.display_name
                              }}</span>
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="
                                equipmemntArr.contact_person &&
                                equipmemntArr.contact_person.primary_phone
                              "
                            >
                              <v-icon small color="green darken-1" class="mr-2"
                                >mdi-phone</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.primary_phone
                              }}</span>
                            </div>
                            <div
                              class="d-flex align-center text-body-1 py-1"
                              v-if="
                                equipmemntArr.contact_person &&
                                equipmemntArr.contact_person.primary_email
                              "
                            >
                              <v-icon small color="blue darken-1" class="mr-2"
                                >mdi-email</v-icon
                              >
                              <span>{{
                                equipmemntArr.contact_person.primary_email
                              }}</span>
                            </div>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <div
                              class="text-caption-1 font-weight-bold text-uppercase text--secondary mb-1"
                            >
                              Company Address
                            </div>
                            <div
                              class="d-flex align-start text-body-1 py-1"
                              v-if="equipmemntArr.property_address"
                            >
                              <v-icon
                                small
                                color="red darken-1"
                                class="mr-2 mt-1"
                                >mdi-map-marker</v-icon
                              >
                              <div>
                                <span v-if="equipmemntArr.street_1"
                                  >{{ equipmemntArr.street_1 }},<br
                                /></span>
                                <span v-if="equipmemntArr.street_2"
                                  >{{ equipmemntArr.street_2 }},
                                </span>
                                <span v-if="equipmemntArr.unit_no"
                                  >{{ equipmemntArr.unit_no }},<br
                                /></span>
                                <span
                                  >{{ equipmemntArr.country }},
                                  {{ equipmemntArr.zip_code }}</span
                                >
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Section 2: Warranty Terms & Financials -->
                <div class="erp-form-section mb-6">
                  <div class="section-title d-flex align-center mb-3">
                    <v-icon small color="cyan darken-2" class="mr-2"
                      >mdi-calendar-clock</v-icon
                    >
                    <span
                      class="text-subtitle-1 font-weight-bold text-uppercase custom-header-blue-text"
                      >Warranty Terms & Financials</span
                    >
                  </div>

                  <v-row>
                    <!-- Duration -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 d-block mb-1">
                        Duration
                      </label>
                      <v-text-field
                        type="number"
                        filled
                        solo
                        flat
                        dense
                        placeholder="e.g. 12"
                        color="cyan"
                        v-model.trim="warrantyOption.duration"
                        v-mask="'###'"
                        v-on:keyup="
                          (e) => updateWarrantyDuration(e, 'warranty_duration')
                        "
                        :min="0"
                        oninput="if(this.value < 0) this.value = 0;"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>

                    <!-- Duration Type -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 d-block mb-1">
                        Duration Type
                      </label>
                      <v-select
                        :items="warrantyDurationTypes"
                        dense
                        filled
                        solo
                        flat
                        v-model.trim="warrantyOption.duration_type"
                        placeholder="Select Duration Type"
                        color="cyan"
                        item-color="cyan"
                        item-text="text"
                        item-value="value"
                        v-on:change="
                          (e) =>
                            updateWarrantyDuration(e, 'warranty_duration_type')
                        "
                        hide-details="auto"
                      ></v-select>
                    </v-col>

                    <!-- Start Date -->
                    <v-col
                      md="6"
                      cols="12"
                      class="py-2"
                      v-if="warrantyOption.equipemt_type != 'parts'"
                    >
                      <label class="font-size-16 font-weight-500 d-block mb-1">
                        Start Date
                      </label>
                      <DatePicker
                        :defaultDate="warrantyOption.start_date"
                        solo
                        :pageLoading="pageLoading"
                        :placeholder="'Start Date'"
                        v-model="warrantyOption.start_date"
                        v-on:change="
                          (e) => updateWarrantyDuration(e, 'start_date')
                        "
                      ></DatePicker>
                    </v-col>

                    <!-- End Date -->
                    <v-col
                      md="6"
                      cols="12"
                      class="py-2"
                      v-if="warrantyOption.equipemt_type != 'parts'"
                    >
                      <label class="font-size-16 font-weight-500 d-block mb-1">
                        Calculated End Date
                      </label>
                      <div
                        class="pa-2 rounded border grey lighten-5 d-flex align-center"
                      >
                        <v-icon small color="cyan darken-2" class="mr-2"
                          >mdi-calendar-check</v-icon
                        >
                        <span
                          class="font-weight-bold text-body-1 color-custom-blue"
                        >
                          {{ formatDate(warrantyOption.end_date) || "N/A" }}
                        </span>
                      </div>
                    </v-col>

                    <!-- Reference No -->
                    <v-col md="6" cols="12" class="py-2">
                      <label
                        class="font-size-16 font-weight-500 d-block mb-1 required"
                      >
                        Reference No.
                      </label>
                      <TextField
                        id="reference"
                        dense
                        filled
                        placeholder="Enter Reference No."
                        solo
                        flat
                        counter="50"
                        v-model="warrantyOption.reference"
                        color="cyan"
                        :maxlength="50"
                      />
                      <v-text-field
                        v-if="false"
                        dense
                        filled
                        solo
                        flat
                        :rules="[
                          validateRules.required(
                            warrantyOption.reference,
                            'Reference No.'
                          ),
                          validateRules.maxLength(
                            warrantyOption.reference,
                            'Reference No.',
                            100
                          ),
                        ]"
                        :maxlength="50"
                        color="cyan"
                        placeholder="Enter Reference No."
                        v-model.trim="warrantyOption.reference"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>

                    <!-- Cost -->
                    <v-col md="6" cols="12" class="py-2">
                      <label class="font-size-16 font-weight-500 d-block mb-1">
                        Cost ($)
                      </label>
                      <PriceField
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="0.00"
                        v-model.trim="warrantyOption.cost"
                        hide-details="auto"
                        prepend-inner-icon="mdi-currency-usd"
                      ></PriceField>
                    </v-col>

                    <!-- Description -->
                    <v-col md="12" cols="12" class="py-2">
                      <div
                        class="d-flex justify-space-between align-center mb-1"
                      >
                        <label class="font-size-16 font-weight-500"
                          >Description / Internal Remarks</label
                        >
                      </div>
                      <v-textarea
                        v-model.trim="warrantyOption.description"
                        auto-grow
                        dense
                        filled
                        solo
                        flat
                        rows="3"
                        v-on:keypress="(e) => manageLimit(e)"
                        v-on:paste="(e) => onPaste(e)"
                        color="cyan"
                        placeholder="Enter warranty description or remarks..."
                        hide-details="auto"
                      ></v-textarea>
                      <div class="d-flex justify-content-end align-center mt-1">
                        <span class="text-caption text--secondary">
                          {{
                            warrantyOption.description
                              ? warrantyOption.description.length
                              : 0
                          }}/200
                        </span>
                      </div>
                    </v-col>

                    <!-- Attachments -->
                    <v-col md="12" cols="12" class="py-2">
                      <div class="section-title d-flex align-center mb-3">
                        <v-icon small color="cyan darken-2" class="mr-2"
                          >mdi-paperclip</v-icon
                        >
                        <span
                          class="text-subtitle-1 font-weight-bold text-uppercase custom-header-blue-text"
                          >Attachments & Documents</span
                        >
                      </div>
                      <DocumnetList
                        v-model="files"
                        :document-type="docType"
                        :extensive="false"
                        :upload-immediately="false"
                      >
                      </DocumnetList>
                    </v-col>
                  </v-row>
                </div>
              </v-container>
            </v-form>
          </perfect-scrollbar>
        </v-card-text>
        <v-divider class="my-0"></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <div class="warrant-manage-action">
            <v-btn
              depressed
              :loading="pageLoading"
              :disabled="pageLoading"
              v-on:click="$emit('close:dialog')"
              class="mx-2 custom-grey-border custom-bold-button"
              >Cancel
            </v-btn>
            <v-btn
              depressed
              :disabled="!formValid || pageLoading"
              :loading="pageLoading"
              v-on:click="update_or_create"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              >Save
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}
.scroll {
  margin-right: -24px;
  padding-right: 24px;
}
.customer-option-subtitle {
  padding-left: 10px;
}
.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: 18px;
  padding: 0;
}
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.border-top {
  border-top: 1px solid #e2e8f0 !important;
}
.border-dashed {
  border: 1px dashed #cbd5e1 !important;
}
.form-field-label {
  font-size: 13px;
  color: #475569;
}
.form-field-label.required::after {
  content: " *";
  color: #ef4444;
}
</style>

<script>
import moment from "moment";
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import {
  POST,
  PUT,
  CLEAR_ERROR,
  QUERY,
  GET,
} from "@/core/services/store/request.module";
import CommonMixin from "@/core/plugins/common-mixin";

import ValidationMixin from "@/core/plugins/validation-mixin";
import { toSafeInteger, cloneDeep } from "lodash";
import Barcode from "@/view/pages/partials/Barcode.vue";
import RadioInputCustom from "@/view/components/RadioInputCustom.vue";
import DocumnetList from "@/view/pages/DocumnetList";
export default {
  name: "create-or-update-warranty",
  mixins: [ValidationMixin, CommonMixin],
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    /*   allCustomerList: {
      type: Array,
      default: () => {
        return new Array();
      },
    },
    allEquipemtList: {
      type: Array,
      default: () => {
        return new Array();
      },
    }, */
    warrantyDetail: {
      type: Object,
      default: () => {
        return new Object();
      },
    },
  },
  components: {
    DatePicker,
    Barcode,
    DocumnetList,
    RadioInputCustom,
  },
  watch: {
    dialogStatus: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.getAllCustomers();
          this.getCustomerEquipment();
          this.getOptions();
          this.updateWarrantyDuration(this.warrantyOption.start_date);
        }
      },
    },
  },
  data() {
    return {
      docType: 115,
      customerId: 0,
      locationId: 0,
      barcode: null,
      formValid: true,
      pageLoading: false,
      startDatePicker: null,
      defaultItemWarranty: new Array(),
      allCustomerList: [],
      allSiteLocationList: [],
      equipmemntArr: {},
      partArr: {},
      allEquipemtList: [],
      allPartLists: [],
      equipmentInfo: {},
      partInfo: {},
      ifWarrentyExist: false,
      files: [],

      warrantyOption: new Object({
        id: null,
        customer: 0,
        equipemt: 0,
        cost: 0,
        warranty_type: "customer",
        start_date: moment().format("YYYY-MM-DD"),
        duration: 12,
        duration_type: "month",
        end_date: moment().format("YYYY-MM-DD"),
        unique_id: null,
        reference: null,
        description: null,
        equipemt_type: "parts",
      }),
      durationList: [
        {
          text: "Day",
          value: "day",
        },
        {
          text: "Week",
          value: "week",
        },
        {
          text: "Month",
          value: "month",
        },
      ],
      warrantyDurationTypes: [
        {
          text: "Day(s)",
          value: "day",
        },
        {
          text: "Month(s)",
          value: "month",
        },
        {
          text: "Year(s)",
          value: "year",
        },
      ],
      /*   warrantyList: [
        { text: "30 Days", value: "30", type: "day" },
        { text: "6 Months", value: "182", type: "day" },
        { text: "1 Year", value: "365", type: "day" },
        { text: "2 Years", value: "730", type: "day" },
        { text: "3 Years", value: "1095", type: "day" },
      ], */
      /*   warrantyDurationTypes: [
        { text: "Day(s)", value: "day" },
        { text: "Week(s)", value: "week" },
        { text: "Month(s)", value: "month" },
        { text: "Year(s)", value: "year" },
      ], */
    };
  },
  methods: {
    getPlaceHolder(type) {
      if (type == "tool") {
        return "Tool";
      } else if (type == "customer-asset") {
        return "Equipment";
      } else {
        return "Own Asset";
      }
    },
    customFilter(item, queryText) {
      const textOne = item.name.toLowerCase();
      const textTwo = item.barcode.toLowerCase();
      const searchText = queryText.toLowerCase();
      return (
        textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1
      );
    },
    // validateNumber(number=0){
    //   console.log('tet')
    //   //if(this.value < 0) this.value = 0;
    //   if(number<0)
    //   return false;

    // },
    getColor(status) {
      if (status == 1) {
        return "cyan";
      }
      if (status == 2) {
        return "green";
      }
      if (status == 3) {
        return "orange";
      }
      if (status == 4) {
        return "red";
      }
      if (status == 5) {
        return "teal";
      }
      return null;
    },
    get_formatted_property(row) {
      let tempArr = [];
      if (row.property_name) {
        tempArr.push(row.property_name);
      }
      if (row.unit_no) {
        tempArr.push(row.unit_no);
      }
      if (row.street_1) {
        tempArr.push(row.street_1);
      }
      if (row.street_2) {
        tempArr.push(row.street_2);
      }
      if (row.zip_code) {
        tempArr.push(row.zip_code);
      }
      if (row.country_name) {
        tempArr.push(row.country_name);
      }
      return tempArr.join(", ");
    },
    clearAll() {
      //console.log('tst')
      this.warrantyOption.customer = 0;
      this.warrantyOption.equipemt = 0;
      this.getAllCustomers();
      this.getCustomerEquipment();
    },
    updateWarrantyDuration(e, type) {
      let warranty_start_date = e;
      if (type != "start_date") {
        warranty_start_date =
          this.warrantyOption && this.warrantyOption.start_date
            ? this.warrantyOption.start_date
            : null;
      }
      if (warranty_start_date) {
        let date = moment(warranty_start_date, "YYYY-MM-DD");

        let duration = this.warrantyOption.duration
          ? toSafeInteger(this.warrantyOption.duration)
          : 0;
        switch (this.warrantyOption.duration_type) {
          case "day":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "d");
            break;
          case "month":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "M");
            break;
          case "year":
            date = moment(warranty_start_date, "YYYY-MM-DD").add(duration, "y");
            break;
        }
        /* this.$nextTick(() => {
					this.warranty.warranty_end_date = date.format("YYYY-MM-DD");
				}); */
        date.subtract(1, "days");
        this.warrantyOption.end_date = cloneDeep(date.format("YYYY-MM-DD"));
        // console.log(this.warrantyOption.end_date);
      }
    },
    onPaste(e) {
      let coppied = e.clipboardData.getData("Text");
      let preVal = this.warrantyOption.description;
      let finalval = String(preVal) + String(coppied);
      if (Number(finalval.length) > 199) {
        let trimValue = finalval.substring(0, 200);
        this.warrantyOption.description = trimValue;
        e.preventDefault();
      }
    },

    manageLimit(e) {
      if (
        this.warrantyOption.description &&
        this.warrantyOption.description.length > 199
      ) {
        e.preventDefault();
      }
    },
    updateFile(index, file, value, data) {
      if (value == "keyword") {
        if (file && file.name) {
          data[index].name = file.name.split(".").slice(0, -1).join(".");
          data[index].suffix = `.${file.name.split(".").pop()}`;
        } else {
          data[index].name = null;
          data[index].suffix = null;
        }
      } else {
        if (file && file.name) {
          this.files[index].name = file.name.split(".").slice(0, -1).join(".");
          this.files[index].suffix = `.${file.name.split(".").pop()}`;
        } else {
          this.files[index].name = null;
          this.files[index].suffix = null;
        }
      }
    },
    addMore() {
      this.files.push({
        file: null,
        name: null,
        remark: null,
        suffix: null,
        tags: [],
        search: null,
        start_date: null,
        end_date: null,
        reminder_date: null,
      });
    },

    removeFile(index) {
      this.files.splice(index, 1);
      if (this.files.length < 1) {
        this.addMore();
      }
    },
    getAllCustomers() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "customer-list" })
        .then(({ data }) => {
          _this.allCustomerList = data;
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
    getOptions(id, type = "customer") {
      const _this = this;
      if (type == "customer") {
        _this.customerId = id ? id : 0;
      }
      if (type == "location") {
        _this.locationId = id ? id : 0;
      }
      if (!_this.customerId) {
        _this.locationId = 0;
      }
      _this.$store
        .dispatch(QUERY, {
          url: "warranty/options",
          //data: { customer: id ? id : 0 },
          data: {
            customer: _this.customerId ? _this.customerId : 0,
            location: _this.locationId ? _this.locationId : 0,
            type: _this.warrantyOption.equipemt_type
              ? _this.warrantyOption.equipemt_type
              : null,
          },
        })
        .then(({ data }) => {
          _this.barcode = data;
          this.warrantyOption.unique_id = _this.barcode.barcode;
          /*  if (data && (_this.customerId > 0 || _this.locationId > 0)) {
            _this.allEquipemtList = data.equipmentArr;
          } */
          _this.allEquipemtList = data.equipmentArr;
          _this.allPartLists = data.parts;
          if (data) {
            _this.allSiteLocationList = data.address ? data.address : [];
          }
        })
        .catch((error) => {
          _this.logError(error);
        });
    },

    getPart(id) {
      if (id && id > 0) {
        const _this = this;
        _this.$store
          .dispatch(QUERY, { url: `parts/${id}` })
          .then(({ data }) => {
            this.partInfo = data;
            this.$nextTick(() => {
              this.ifWarrentyExist = data.has_warranty;
            });
            this.partArr = data.property;
            _this.warrantyOption.customer = 0;
            _this.warrantyOption.address = 0;
          })
          .catch((error) => {
            _this.logError(error);
          });
      }
    },
    getEquipment(idOrObj) {
      const _this = this;
      let equipmentId = 0;

      if (!idOrObj) {
        this.equipmentInfo = {};
        this.equipmemntArr = {};
        return;
      }

      if (typeof idOrObj === "object") {
        equipmentId = idOrObj.id ? idOrObj.id : 0;
      } else {
        equipmentId = Number(idOrObj);
      }

      if (!equipmentId || equipmentId <= 0) {
        this.equipmentInfo = {};
        this.equipmemntArr = {};
        return;
      }

      _this.$store
        .dispatch(QUERY, { url: `equipment/${equipmentId}` })
        .then(({ data }) => {
          this.equipmentInfo = data;
          this.equipmemntArr = data.property || {};

          if (data && data.customer) {
            _this.warrantyOption.customer = data.customer.id;
            _this.getOptions(data.customer.id, "customer"); //call on equipment change for site locations

            if (!_this.locationId) {
              _this.warrantyOption.address = data.property
                ? data.property.id
                : 0;
            }
          }
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
    getCustomerEquipment() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "equipment-list" })
        .then(({ data }) => {
          _this.allEquipemtList = data;
        })
        .catch((error) => {
          _this.logError(error);
        });
    },
    update_or_create() {
      const _this = this;
      if (!_this.$refs.warrantyForm.validate()) {
        return false;
      }
      _this.$store.dispatch(CLEAR_ERROR, {});
      _this.pageLoading = true;
      let formData = new FormData();
      formData.append(
        "type",
        this.warrantyOption.warrantyOption
          ? this.warrantyOption.warrantyOption
          : "customer"
      );
      formData.append(
        "type_id",
        this.warrantyOption.customer ? this.warrantyOption.customer : 0
      );

      formData.append(
        "site_location",
        this.warrantyOption.address ? this.warrantyOption.address : 0
      );

      formData.append(
        "service_id",
        this.warrantyOption.equipemt ? this.warrantyOption.equipemt.id : 0
      );
      formData.append(
        "duration",
        this.warrantyOption.duration ? this.warrantyOption.duration : 0
      );
      formData.append(
        "duration_type",
        this.warrantyOption.duration_type
          ? this.warrantyOption.duration_type
          : "day"
      );
      if (this.warrantyOption && this.warrantyOption.start_date) {
        // changes
        if (this.warrantyOption.equipemt_type != "parts") {
          formData.append(
            "start_date",
            this.warrantyOption.start_date
              ? this.warrantyOption.start_date
              : null
          );
        } else {
          formData.append("start_date", null);
        }
      }

      if (this.warrantyOption && this.warrantyOption.end_date) {
        if (this.warrantyOption.equipemt_type != "parts") {
          formData.append(
            "end_date",
            this.warrantyOption.end_date ? this.warrantyOption.end_date : null
          );
        } else {
          formData.append("end_date", null);
        }
      }
      if (this.warrantyOption.equipemt_type == "parts") {
        this.warrantyOption.type = "";
      }
      if (this.warrantyOption && this.warrantyOption.cost) {
        formData.append(
          "cost",
          this.warrantyOption.cost ? this.warrantyOption.cost : 0
        );
      }
      if (this.warrantyOption && this.warrantyOption.description) {
        formData.append(
          "remark",
          this.warrantyOption.description
            ? this.warrantyOption.description
            : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.equipemt_type) {
        formData.append(
          "equipemt_type",
          this.warrantyOption.equipemt_type
            ? this.warrantyOption.equipemt_type
            : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.reference) {
        formData.append(
          "reference",
          this.warrantyOption.reference ? this.warrantyOption.reference : null
        );
      }
      if (this.warrantyOption && this.warrantyOption.unique_id) {
        formData.append(
          "unique_id",
          this.warrantyOption.unique_id ? this.warrantyOption.unique_id : null
        );
      }
      for (let i = 0; i < this.files.length; i++) {
        if (this.files && this.files[i] && this.files[i].file) {
          formData.append(`file[${i}][file]`, this.files[i].file);
          formData.append(`file[${i}][name]`, this.files[i].name);
          formData.append(`file[${i}][suffix]`, this.files[i].suffix);
          formData.append(`file[${i}][remark]`, this.files[i].remark);
          // if (this.files[i]?.tags) {
          //   console.log(this.files[i].tags);

          //   formData.append(`file[${i}][tags]`, JSON.stringify(this.files[i].tags));
          // }
          if (this.files[i]?.tags) {
            for (let j = 0; j < this.files[i]?.tags?.length; j++) {
              formData.append(
                `file[${i}][tags][${j}][text]`,
                this.lodash.toString(this.files[i].tags[j].text)
              );
              formData.append(
                `file[${i}][tags][${j}][color]`,
                this.lodash.toString(this.files[i].tags[j].color)
              );
            }
          }

          formData.append(`file[${i}][start_date]`, this.files[i].start_date);
          formData.append(`file[${i}][end_date]`, this.files[i].end_date);
          formData.append(
            `file[${i}][reminder_date]`,
            this.files[i].reminder_date
          );
        }
      }

      let requestTYPE = POST;
      let requestURL = "warranty";
      this.$store
        .dispatch(requestTYPE, {
          url: requestURL,
          data: formData,
        })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    updateWarranty() {
      const _this = this;
      if (!_this.$refs.warrantyForm.validate()) {
        return false;
      }

      _this.$store.dispatch(CLEAR_ERROR, {});
      _this.pageLoading = true;
      let formData = new Object({
        id: _this.lodash.toSafeInteger(_this.warrantyOption.id) || null,
        customer:
          _this.lodash.toSafeInteger(_this.warrantyOption.customer) || null,
        product:
          _this.lodash.toSafeInteger(_this.warrantyOption.product) || null,
        start_date: _this.warrantyOption.start_date,
        unique_id: _this.warrantyOption.unique_id,
        description: _this.warrantyOption.description,
        warranty_data: _this.defaultItemWarranty,
        duration: _this.defaultItemWarranty,
      });

      let requestType = POST;
      if (_this.lodash.toSafeInteger(formData.id) > 0) {
        requestType = PUT;
      }

      _this.$store
        .dispatch(requestType, { url: "warranty", data: formData })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  computed: {
    /*  warrantDefaultStartDate() {
        que_id,
        description: _this.warrantyOption.description,
        warranty_data: _this.defaultItemWarranty,
        duration: _this.defaultItemWarranty,
      });

      let requestType = POST;
      if (_this.lodash.toSafeInteger(formData.id) > 0) {
        requestType = PUT;
      }

      _this.$store
        .dispatch(requestType, { url: "warranty", data: formData })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  computed: {
   /*  warrantDefaultStartDate() {
 if (_this.lodash.toSafeInteger(formData.id) > 0) {
        requestType = PUT;
      }

      _this.$store
        .dispatch(requestType, { url: "warranty", data: formData })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  computed: {
   /*  warrantDefaultStartDate() {
   if (_this.lodash.toSafeInteger(formData.id) > 0) {
        requestType = PUT;
      }

      _this.$store
        .dispatch(requestType, { url: "warranty", data: formData })
        .then(() => {
          _this.$emit("update:warranty", true);
          _this.$emit("close:dialog");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  computed: {
   /*  warrantDefaultStartDate() {
      return this.lodash.isEmpty(this.warrantyDetail) === false
        ? this.warrantyDetail.warranty_start_date
        : new Date().toISOString().substr(0, 10);
    }, */
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 60);
    },
  },
};
</script>
